import { ChangeEvent, useEffect, useState } from 'react';

import { adjustInputCursor } from 'helpers/adjustInputCursor';
import { Input, InputProps, isFloat } from 'libs/ui';

import { FieldType } from '../types';
import { useField } from '../useField';

import { formatDecimalValue, parseDecimalValue } from './helpers';

export interface DecimalFieldProps
  extends FieldType,
    Omit<InputProps, 'name' | 'onChange' | 'onChangeText' | 'value'> {
  maxFloatingPoints?: number;
  maxIntegerLength?: number;
}

/**
 * Decimal field.
 */
export const DecimalField = ({
  name,
  allowSubmit,
  maxFloatingPoints = 2,
  maxIntegerLength = 13,
  readOnly,
  ...props
}: DecimalFieldProps) => {
  const { field, helpers, inputProps } = useField<string | undefined>(name);

  const [value, setValue] = useState(
    formatDecimalValue(field.value, maxFloatingPoints, maxIntegerLength)
  );

  useEffect(() => {
    if (field.value === undefined || parseDecimalValue(value) !== field.value) {
      setValue(
        formatDecimalValue(field.value, maxFloatingPoints, maxIntegerLength)
      );
    }
  }, [field.value, maxFloatingPoints, maxIntegerLength, value]);

  const handleChange = (
    newValue: string,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    if (!isFloat(newValue, maxFloatingPoints)) return;

    const value = /[,.]0?$/.test(newValue)
      ? newValue.replace('.', ',')
      : formatDecimalValue(newValue, maxFloatingPoints, maxIntegerLength);

    setValue(value);

    adjustInputCursor(event, value, newValue);

    const fieldValue = parseDecimalValue(value);

    helpers.setValue(fieldValue);
  };

  return (
    <Input
      {...props}
      fullWidth
      name={name}
      value={value}
      onBlur={field.onBlur(name)}
      onChange={() => {
        helpers.setError('');
      }}
      onChangeText={handleChange}
      onClear={() => helpers.setValue(undefined)}
      {...inputProps}
      readOnly={readOnly || inputProps.readOnly}
    />
  );
};
